import type { JSX } from 'react';

type FunctionType = (a: unknown) => unknown;

/**
 * chains React HOCs into one wrapper around a base component.
 * HOCs run right-to-left (`reduceRight`), so the first argument is the outermost layer.
 * `undefined` entries are dropped (e.g. platform-conditional HOCs).
 *
 * @example
 * const BlockWithPipeline = compose(withContext, withBlockState, withPortalRenderer)(
 *   BlockRenderer,
 * );
 * // same as: withContext(withBlockState(withPortalRenderer(BlockRenderer)))
 */
export const compose = <T>(...fns: (FunctionType | undefined)[]) => {
  const filteredFns = fns.filter((fn) => fn !== undefined);
  return (component: (props: unknown) => JSX.Element | null) =>
    filteredFns.reduceRight((acc, fn) => fn(acc), component) as T;
};
